var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import { livequeueChatService } from 'ziphy-web-shared/basic/api';
import { useDebouncedValue } from 'ziphy-web-shared/basic/hooks';
import { usePaginationInfinite } from 'ziphy-web-shared/basic/hooks/usePaginationInfinite';
import { WithLoaderWrapper } from 'ziphy-web-shared/basic/lib/loaders';
import { PageNoResults } from 'ziphy-web-shared/basic/lib/simple';
import { formatDate } from 'ziphy-web-shared/basic/utils/time';
import { getDashboardProgramName, getDashboardStart, getDashboardType, } from '@nedoShared/modules/dashboard/dashboard.helper';
import ActivityCard from '@nedoShared/modules/dashboard/dashboardActivity/activityCards/item/ActivityCard';
import ActivityReminder from '@nedoShared/modules/dashboard/dashboardActivity/activityReminder/ActivityReminder';
import styles from './ActivityCards.module.scss';
const ActivityCards = ({ filters }) => {
    const [rawItems, setRawItems] = useState([]);
    const localTZ = moment.tz.guess();
    const now = moment.tz(localTZ);
    const textQuery = useDebouncedValue(filters.search, 300);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
    }, [filters.type, filters.dateFrom, filters.dateTo, textQuery]);
    const { ref, isLoading } = usePaginationInfinite({
        fetchData: ({ limit, offset }) => __awaiter(void 0, void 0, void 0, function* () {
            const last = rawItems[rawItems.length - 1];
            const getTypes = () => {
                const allTypes = [
                    'telehealth_appointment',
                    'telehealth_appointment_reminder',
                    'lab_results',
                    'provider_assignment',
                ];
                if (filters.type === 'all') {
                    return allTypes;
                }
                if (filters.type === 'telehealth_appointment') {
                    return ['telehealth_appointment', 'telehealth_appointment_reminder'];
                }
                if (filters.type === 'lab_results') {
                    return ['lab_results', 'telehealth_appointment_reminder'];
                }
                if (filters.type === 'provider_assignment') {
                    return ['provider_assignment', 'telehealth_appointment_reminder'];
                }
                return allTypes;
            };
            const params = {
                before_message_id: offset > 0 && last ? last._id : undefined,
                sort_direction: '-1',
                attachment_types: getTypes(),
                text_query: textQuery ? textQuery : undefined,
                after_datetime: filters.dateFrom
                    ? moment(filters.dateFrom).utc().startOf('day').toISOString()
                    : undefined,
                before_datetime: filters.dateTo
                    ? moment(filters.dateTo).endOf('day').utc().toISOString()
                    : undefined,
                limit,
            };
            const { data } = yield livequeueChatService.getDashboardItems(params);
            setLoading(false);
            return data.slice().reverse();
        }),
        limit: 10,
        idKeyPath: '_id',
        externalData: rawItems,
        setExternalData: setRawItems,
    }, [filters.type, filters.dateFrom, filters.dateTo, textQuery]);
    const grouped = useMemo(() => {
        return rawItems
            .filter((el) => getDashboardType(el) !== 'teleReminder')
            .reduce((acc, item) => {
            var _a;
            const dateKey = (_a = moment(getDashboardStart(item)).tz(localTZ)) === null || _a === void 0 ? void 0 : _a.format('YYYY-MM-DD');
            if (!acc[dateKey])
                acc[dateKey] = [];
            acc[dateKey].push(item);
            return acc;
        }, {});
    }, [rawItems, localTZ]);
    const sortedDates = useMemo(() => {
        const tz = localTZ;
        const today = moment.tz(tz).startOf('day');
        const normalize = (d) => moment.tz(d, tz).startOf('day');
        const dates = Object.keys(grouped);
        const todayArr = dates.filter((d) => normalize(d).isSame(today, 'day'));
        const future = dates
            .filter((d) => normalize(d).isAfter(today))
            .sort((a, b) => normalize(b).diff(normalize(a)));
        const past = dates
            .filter((d) => normalize(d).isBefore(today))
            .sort((a, b) => normalize(b).diff(normalize(a)));
        return [...future, ...todayArr, ...past];
    }, [grouped, localTZ]);
    const getDateLabel = (dateStr) => {
        const date = moment.tz(dateStr, localTZ);
        const today = moment.tz(localTZ).startOf('day');
        const yesterday = today.clone().subtract(1, 'day');
        if (date.isSame(today, 'day'))
            return 'Today';
        if (date.isSame(yesterday, 'day'))
            return 'Yesterday';
        return formatDate(date, { format: 'dateNoYear', timezone: localTZ });
    };
    const isTeleReminder = (att) => {
        return att.type === 'telehealth_appointment_reminder';
    };
    const showReminder = useMemo(() => {
        if (!rawItems.length)
            return null;
        const items = rawItems.filter((msg) => getDashboardType(msg) === 'teleReminder');
        if (!items.length)
            return null;
        const reminderMsg = items.sort((a, b) => Math.abs(moment(getDashboardStart(a)).diff(now)) -
            Math.abs(moment(getDashboardStart(b)).diff(now)))[0];
        const reminderAtt = reminderMsg.attachments.find((att) => att.type === 'telehealth_appointment_reminder');
        if (!reminderAtt)
            return null;
        const tele = reminderMsg.consultation.attachments.find((x) => x.type === 'telehealth_appointment');
        if (!tele)
            return null;
        const start = tele.appointed_start;
        if (moment(start).isBefore(moment()))
            return null;
        return {
            id: reminderAtt.telehealth_appointment_id,
            type: reminderAtt.type,
            start,
            programName: getDashboardProgramName(reminderMsg),
        };
    }, [rawItems]);
    const todayRef = useRef(null);
    useEffect(() => {
        if (todayRef.current) {
            todayRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
    }, [sortedDates.length]);
    return (_jsx(WithLoaderWrapper, Object.assign({ isLoading: isLoading, mode: !rawItems.length ? 'update' : 'silent', loaderMode: 'absoluteCenter', withWrapper: false }, { children: _jsxs("div", Object.assign({ className: styles.cards }, { children: [showReminder && _jsx(ActivityReminder, { reminder: showReminder }), !isLoading && !loading && !rawItems.length && _jsx(PageNoResults, {}), _jsxs("div", Object.assign({ className: styles.content }, { children: [sortedDates.map((dateKey) => {
                            const isToday = moment.tz(localTZ).isSame(moment.tz(dateKey, localTZ), 'day');
                            return (_jsxs("div", Object.assign({ ref: isToday ? todayRef : undefined }, { children: [_jsx("div", Object.assign({ className: styles.dateLabel }, { children: getDateLabel(dateKey) })), grouped[dateKey].map((item) => (_jsx(ActivityCard, { item: item }, item._id)))] }), dateKey));
                        }), _jsx("div", { ref: ref, style: { height: 1 } })] }))] })) })));
};
export default observer(ActivityCards);
